import { useDashboardAudience } from './dashboardAudience'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON } from '@/lib/api'
import type { MemberPortalOrganization } from './MemberSignIn'
import { usePortalActivityDownload } from './usePortalActivityDownload'

type GroupRow = { name: string; people: number; members: string[]; person_match: boolean }
type GroupsData = { rows: GroupRow[]; total: number; available_total: number }

export function MemberPortalGroups({
  organization,
  viewer,
}: {
  organization: MemberPortalOrganization
  viewer: string
}) {
  const audience = useDashboardAudience(organization.id)
  const { search, sort, base, request, path, change } = useGroupsState(organization.id)
  const query = useQuery({
    queryKey: [
      audience.preview ? 'organization-dashboard-preview' : 'member-portal-dashboard',
      viewer,
      organization.id,
      'groups',
      path,
    ],
    queryFn: () => getJSON<{ groups: GroupsData }>(path),
    retry: false,
  })
  const csv = usePortalActivityDownload(`${base}.csv?${request}`, 'organization-groups.csv')
  const data = query.isError ? undefined : query.data?.groups
  if (
    !audience.preview &&
    (csv.forbidden || (query.error instanceof ApiError && query.error.status === 404))
  )
    return <Navigate to="/portal" replace />
  return (
    <Card className="relative overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <h2 className="text-body font-medium">Groups</h2>
            {data && (
              <span className="text-caption text-muted-foreground">
                · {data.available_total} groups
              </span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!data || csv.pending}
            onClick={() => void csv.download()}
          >
            <Download className="mr-1.5 size-3.5" aria-hidden="true" />
            CSV
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative w-56 max-w-full">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              className="h-9 pl-8"
              aria-label="Person or group"
              placeholder="Person or group"
              value={search}
              onChange={(event) => change('q', event.target.value)}
            />
          </div>
          <Select value={sort} onValueChange={(value) => change('sort', value)}>
            <SelectTrigger aria-label="Sort groups" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="people">Sort: People</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {csv.error && <Alert variant="destructive">CSV unavailable. Try downloading again.</Alert>}
      {query.isPending && <EmptyState variant="quiet" title="Loading groups" />}
      {query.isError && (
        <Alert variant="destructive">
          Unable to load groups.{' '}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      )}
      {data && (
        <GroupResults
          key={search}
          data={data}
          searching={Boolean(search)}
          clear={() => change('q', '')}
        />
      )}
    </Card>
  )
}

function GroupResults({
  data,
  searching,
  clear,
}: {
  data: GroupsData
  searching: boolean
  clear: () => void
}) {
  const [first] = useState(data.rows[0]?.name)
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  return (
    <>
      {data.rows.length ? (
        <div className="divide-y divide-border">
          {data.rows.map((group) => {
            const open =
              toggled[group.name] ?? (searching ? group.person_match : group.name === first)
            const Chevron = open ? ChevronDown : ChevronRight
            return (
              <Collapsible
                key={group.name}
                open={open}
                onOpenChange={(value) =>
                  setToggled((previous) => ({ ...previous, [group.name]: value }))
                }
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex min-h-9 w-full items-center gap-2 px-4 py-2 text-left text-body-sm hover:bg-muted/50"
                  >
                    <Chevron
                      className="size-3.5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="font-medium">{group.name}</span>{' '}
                    <span className="text-muted-foreground">
                      · {group.people} {group.people === 1 ? 'person' : 'people'}
                    </span>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="space-y-1 pb-3 pl-10 pr-4 text-body-sm">
                    {group.members.map((name, index) => (
                      <li key={`${name}:${index}`}>{name}</li>
                    ))}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            )
          })}
        </div>
      ) : (
        <EmptyState
          variant="quiet"
          title={data.available_total ? 'No rows match' : 'No groups yet'}
          action={
            data.available_total ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      )}
      <div className="border-t border-border px-4 py-3 text-caption text-muted-foreground">
        {data.total ? `Showing 1–${data.total} of ${data.total} groups` : 'Showing 0 of 0 groups'}
      </div>
    </>
  )
}

function useGroupsState(organizationId: number) {
  const [params, setParams] = useSearchParams()
  const search = params.get('q') ?? ''
  const sort = params.get('sort') === 'people' ? 'people' : 'name'
  const request = new URLSearchParams()
  if (search) request.set('q', search)
  request.set('sort', sort)
  const base = `${useDashboardAudience(organizationId).base}/groups`
  const path = `${base}?${request}`
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    next.delete('cursor')
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }
  return { search, sort, base, request, path, change }
}
